/**
 * Puzzle Class
 *
 * Represents a complete puzzle (rejtvény) with multiple grids.
 * Migrated from the legacy Rejtveny constructor function in olaszmodra.js.
 */

import { Grid } from './Grid';
import { Cell } from './Cell';

/**
 * Mask image for cells (simplified from legacy Kep class)
 */
export interface MaskImage {
    cellId: number; // helye - the cell id where this mask applies
    base64Data: string; // ertek - the base64 image data
}

/**
 * Color scheme for the puzzle
 */
export interface PuzzleColors {
    borderColor: string; // keretszín
    backgroundColor: string; // háttérszín
    textColor: string; // betűszín
}

/**
 * Puzzle configuration
 */
export interface PuzzleConfig {
    type: string;
    datasheet?: string;
    solution?: string;
    colors?: PuzzleColors;
}

/**
 * Clues collection returned by getClues()
 */
export interface CluesCollection {
    horizontal: string; // VÍZSZINTES clues
    vertical: string; // FÜGGŐLEGES clues
    horizontalBridge: string; // horizontal bridge clues
    verticalBridge: string; // vertical bridge clues
}

/**
 * Puzzle class representing a complete crossword puzzle.
 */
export class Puzzle {
    private readonly _type: string;
    private readonly _grids: Grid[] = [];
    private _length = 0;
    private _solution: string | null = null;
    private _maskImages: MaskImage[] = [];
    private _datasheet = '';
    private _colors: PuzzleColors = {
        borderColor: '#000000',
        backgroundColor: '#FFFFFF',
        textColor: '#000000',
    };

    constructor(config: PuzzleConfig) {
        this._type = config.type;
        if (config.datasheet) {
            this._datasheet = config.datasheet;
        }
        if (config.solution) {
            this._solution = config.solution;
        }
        if (config.colors) {
            this._colors = { ...config.colors };
        }
    }

    // ============================================================================
    // Grid Management
    // ============================================================================

    /**
     * Add a grid to the puzzle
     */
    push(grid: Grid): void {
        this._grids.push(grid);
    }

    /**
     * Get grid by array index
     */
    get(index: number): Grid {
        return this._grids[index];
    }

    /**
     * Get all grids
     */
    get grids(): Grid[] {
        return this._grids;
    }

    /**
     * Count the number of grids
     */
    countGrids(): number {
        return this._grids.length;
    }

    /**
     * Find the grid that contains a given cell index
     */
    getGrid(cellIndex: number): Grid | undefined {
        for (const grid of this._grids) {
            if (grid.minId <= cellIndex && grid.maxId >= cellIndex) {
                return grid;
            }
        }
        return undefined;
    }

    /**
     * Get the next grid after the one containing the given cell index
     */
    getNextGrid(cellIndex: number): Grid {
        let nextIndex = 0;

        for (let i = 0; i < this._grids.length; i++) {
            if (
                this._grids[i].minId <= cellIndex &&
                this._grids[i].maxId >= cellIndex
            ) {
                nextIndex = i + 1;
            }
        }

        if (nextIndex === this._grids.length) {
            nextIndex = 0;
        }

        return this._grids[nextIndex];
    }

    // ============================================================================
    // Cell Access
    // ============================================================================

    /**
     * Get a cell by its index
     */
    getCell(cellIndex: number): Cell | undefined {
        const grid = this.getGrid(cellIndex);
        return grid?.get(cellIndex);
    }

    /**
     * Get the corresponding cell in the next grid
     */
    getNextCell(cellIndex: number): Cell | undefined {
        const currentGrid = this.getGrid(cellIndex);
        const nextGrid = this.getNextGrid(cellIndex);

        if (!currentGrid) return undefined;

        const minIdOffset = nextGrid.minId - currentGrid.minId;
        return nextGrid.get(cellIndex + minIdOffset);
    }

    // ============================================================================
    // Type & Length
    // ============================================================================

    get type(): string {
        return this._type;
    }

    get length(): number {
        return this._length;
    }

    set length(value: number) {
        this._length = value;
    }

    // ============================================================================
    // Solution
    // ============================================================================

    get solution(): string | null {
        return this._solution;
    }

    set solution(value: string | null) {
        this._solution = value;
    }

    setSolution(value: string | null): void {
        this._solution = value;
    }

    getSolution(): string | null {
        return this._solution;
    }

    // ============================================================================
    // Mask Images (Graphics)
    // ============================================================================

    /**
     * Set mask images from XML element
     */
    setMaskImagesFromXml(grafikaElement: Element): void {
        const items = grafikaElement.getElementsByTagName('kép');

        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            const cellId = parseInt(item.getAttribute('helye') ?? '0', 10);
            const base64Data = item.textContent ?? '';

            this._maskImages.push({ cellId, base64Data });
        }
    }

    /**
     * Get all mask images
     */
    getMaskImages(): MaskImage[] {
        return [...this._maskImages];
    }

    /**
     * Clear all mask images
     */
    clearMaskImages(): void {
        this._maskImages = [];
    }

    /**
     * Check if there are any mask images
     */
    hasMaskImages(): boolean {
        return this._maskImages.length > 0;
    }

    /**
     * Get the background image CSS for a cell's mask
     */
    getMaskImageCss(cellId: number): string {
        for (const img of this._maskImages) {
            if (img.cellId === cellId) {
                return `url(data:image/jpeg;base64,${img.base64Data})`;
            }
        }
        return '';
    }

    /**
     * Get the base64 data URL for a cell's mask
     */
    getMaskImageDataUrl(cellId: number): string {
        for (const img of this._maskImages) {
            if (img.cellId === cellId) {
                return `data:image/jpeg;base64,${img.base64Data}`;
            }
        }
        return '';
    }

    // ============================================================================
    // Colors
    // ============================================================================

    setColors(
        borderColor: string,
        backgroundColor: string,
        textColor: string,
    ): void {
        this._colors.borderColor = borderColor;
        this._colors.backgroundColor = backgroundColor;
        this._colors.textColor = textColor;
    }

    get colors(): PuzzleColors {
        return { ...this._colors };
    }

    get borderColor(): string {
        return this._colors.borderColor;
    }

    get backgroundColor(): string {
        return this._colors.backgroundColor;
    }

    get textColor(): string {
        return this._colors.textColor;
    }

    // ============================================================================
    // Datasheet
    // ============================================================================

    get datasheet(): string {
        return this._datasheet;
    }

    set datasheet(value: string) {
        this._datasheet = value;
    }

    // ============================================================================
    // Initialization Methods
    // ============================================================================

    /**
     * Initialize expression error flags for all grids.
     * Validates stored expressions against calculated expressions from cell values
     * and enables error flags where they don't match.
     * Migrated from legacy initKifejezesHibak function.
     */
    initExpressionErrors(): void {
        for (const grid of this._grids) {
            grid.initExpressionErrors();
        }
    }

    /**
     * Initialize margins for all grids
     */
    initMargins(): void {
        for (const grid of this._grids) {
            grid.initMargins();
        }
    }

    /**
     * Initialize lines for all grids
     */
    initLines(): void {
        for (const grid of this._grids) {
            grid.initLines();
        }
    }

    // ============================================================================
    // Clues Collection
    // ============================================================================

    /**
     * Get all clues formatted for display
     */
    getClues(): CluesCollection {
        const endMarkers = ['!', '?', '.'];

        // Horizontal clues (VÍZSZINTES)
        const horizontalClues = this._grids[0]?.getVerticalClues() ?? [];
        for (let i = 0; i < horizontalClues.length; i++) {
            horizontalClues[i] = ` <b>${i + 1}.</b> ${horizontalClues[i]}`;
        }
        for (let gridIdx = 1; gridIdx < this._grids.length; gridIdx++) {
            const gridClues = this._grids[gridIdx].getVerticalClues();
            for (let j = 0; j < gridClues.length; j++) {
                if (horizontalClues[j]) {
                    horizontalClues[j] += ' – ' + gridClues[j];
                }
            }
        }
        for (let i = 0; i < horizontalClues.length; i++) {
            const lastChar = horizontalClues[i].charAt(
                horizontalClues[i].length - 5,
            );
            if (!endMarkers.includes(lastChar)) {
                horizontalClues[i] += '.';
            }
        }
        const horizontal = '<b>VÍZSZINTES:</b> ' + horizontalClues.join('');

        // Vertical clues (FÜGGŐLEGES)
        const verticalClues = this._grids[0]?.getHorizontalClues() ?? [];
        for (let i = 0; i < verticalClues.length; i++) {
            verticalClues[i] = ` <b>${i + 1}.</b> ${verticalClues[i]}`;
        }
        for (let gridIdx = 1; gridIdx < this._grids.length; gridIdx++) {
            const gridClues = this._grids[gridIdx].getHorizontalClues();
            for (let j = 0; j < gridClues.length; j++) {
                if (verticalClues[j]) {
                    verticalClues[j] += ' – ' + gridClues[j];
                }
            }
        }
        for (let i = 0; i < verticalClues.length; i++) {
            const lastChar = verticalClues[i].charAt(
                verticalClues[i].length - 5,
            );
            if (!endMarkers.includes(lastChar)) {
                verticalClues[i] += '.';
            }
        }
        const vertical = '<b>FÜGGŐLEGES:</b> ' + verticalClues.join('');

        // Horizontal bridge clues (A hidak meghatározásai - vízszintes)
        let horizontalBridge = '';
        const vhClues = this._grids[0]?.getVerticalBackClues() ?? [];
        if (vhClues.join('') !== '') {
            for (let i = 0; i < vhClues.length; i++) {
                vhClues[i] = ` <b>${i + 1}.</b> ${vhClues[i]}`;
            }
            for (let gridIdx = 1; gridIdx < this._grids.length; gridIdx++) {
                const gridClues = this._grids[gridIdx].getVerticalBackClues();
                for (let j = 0; j < gridClues.length; j++) {
                    if (vhClues[j]) {
                        vhClues[j] += ' – ' + gridClues[j];
                    }
                }
            }
            for (let i = 0; i < vhClues.length; i++) {
                const lastChar = vhClues[i].charAt(vhClues[i].length - 5);
                if (!endMarkers.includes(lastChar)) {
                    vhClues[i] += '.';
                }
            }
            horizontalBridge =
                '<b>A hidak meghatározásai:</b> ' + vhClues.join('');
        }

        // Vertical bridge clues (A hidak meghatározásai - függőleges)
        let verticalBridge = '';
        const fhClues = this._grids[0]?.getHorizontalBackClues() ?? [];
        if (fhClues.join('') !== '') {
            for (let i = 0; i < fhClues.length; i++) {
                fhClues[i] = ` <b>${i + 1}.</b> ${fhClues[i]}`;
            }
            for (let gridIdx = 1; gridIdx < this._grids.length; gridIdx++) {
                const gridClues = this._grids[gridIdx].getHorizontalBackClues();
                for (let j = 0; j < gridClues.length; j++) {
                    if (fhClues[j]) {
                        fhClues[j] += ' – ' + gridClues[j];
                    }
                }
            }
            for (let i = 0; i < fhClues.length; i++) {
                const lastChar = fhClues[i].charAt(fhClues[i].length - 5);
                if (!endMarkers.includes(lastChar)) {
                    fhClues[i] += '.';
                }
            }
            verticalBridge =
                '<b>A hidak meghatározásai:</b> ' + fhClues.join('');
        }

        return {
            horizontal,
            vertical,
            horizontalBridge,
            verticalBridge,
        };
    }

    // ============================================================================
    // Validation
    // ============================================================================

    /**
     * Check if any grid has clue errors
     */
    hasClueErrors(): boolean {
        for (const grid of this._grids) {
            if (grid.checkClueErrors()) {
                return true;
            }
        }
        return false;
    }

    /**
     * Check if any grid has expression errors
     */
    hasExpressionErrors(): boolean {
        for (const grid of this._grids) {
            if (grid.checkExpressionErrors()) {
                return true;
            }
        }
        return false;
    }

    // ============================================================================
    // Rendering
    // ============================================================================

    /**
     * Convert puzzle to HTML string
     * @param showClueMarking - Whether to show clue marking background images
     * @param isHorizontal - Current direction (true = horizontal)
     * @param knownMarking - Whether known marking is on (show values)
     * @param validCharPattern - Regex pattern for valid characters
     */
    toString(
        showClueMarking = false,
        isHorizontal = true,
        knownMarking = true,
        validCharPattern = '',
    ): string {
        return this._grids
            .map((g) =>
                g.toHtml(
                    showClueMarking,
                    isHorizontal,
                    knownMarking,
                    validCharPattern,
                ),
            )
            .join('');
    }

    /**
     * Render the puzzle to HTML
     * @param showClueMarking - Whether to show clue marking background images
     * @param isHorizontal - Current direction (true = horizontal)
     * @param knownMarking - Whether known marking is on (show values)
     * @param validCharPattern - Regex pattern for valid characters
     */
    toHtml(
        showClueMarking = false,
        isHorizontal = true,
        knownMarking = true,
        validCharPattern = '',
    ): string {
        return this.toString(
            showClueMarking,
            isHorizontal,
            knownMarking,
            validCharPattern,
        );
    }

    // ============================================================================
    // XML Serialization
    // ============================================================================

    /**
     * Serialize the puzzle to XML
     */
    toXml(): string {
        let xml = `<?xml version="1.0" encoding="utf-8"?><${this._type}>`;

        // Datasheet
        xml += `<adatlap>${this._datasheet}</adatlap>`;

        // Grids
        for (const grid of this._grids) {
            xml += grid.toXml();
        }

        // Graphics with color scheme
        const colorAttrs =
            ` keretszín="${this._colors.borderColor}"` +
            ` háttérszín="${this._colors.backgroundColor}"` +
            ` betűszín="${this._colors.textColor}"`;

        if (this._maskImages.length > 0) {
            const maskXml = this._maskImages
                .map(
                    (img) =>
                        `<kép helye="${img.cellId}">${img.base64Data}</kép>`,
                )
                .join('');
            xml += `<grafika${colorAttrs}>${maskXml}</grafika>`;
        } else {
            xml += `<grafika${colorAttrs}></grafika>`;
        }

        // Solution
        if (this._solution !== null) {
            xml += `<megoldás>${this._solution}</megoldás>`;
        }

        return xml + `</${this._type}>`;
    }

    // ============================================================================
    // XML Parsing
    // ============================================================================

    /**
     * Parse a puzzle from XML string
     */
    static fromXml(xmlString: string): Puzzle {
        const parser = new DOMParser();
        const doc = parser.parseFromString(xmlString, 'application/xml');

        // Get the root element (puzzle type)
        const root = doc.documentElement;
        const type = root.tagName;

        // Parse datasheet
        const datasheetEl = root.getElementsByTagName('adatlap')[0];
        const datasheet = datasheetEl?.textContent ?? '';

        // Parse solution
        const solutionEl = root.getElementsByTagName('megoldás')[0];
        const solution = solutionEl?.textContent ?? null;

        // Parse colors from grafika element
        const grafikaEl = root.getElementsByTagName('grafika')[0];
        const colors: PuzzleColors = {
            borderColor: grafikaEl?.getAttribute('keretszín') ?? '#000000',
            backgroundColor: grafikaEl?.getAttribute('háttérszín') ?? '#FFFFFF',
            textColor: grafikaEl?.getAttribute('betűszín') ?? '#000000',
        };

        // Create puzzle
        const puzzle = new Puzzle({
            type,
            datasheet,
            solution: solution ?? undefined,
            colors,
        });

        // Parse grids
        const gridElements = root.getElementsByTagName('hálózat');
        let cellIdCounter = 0;

        for (let gridIdx = 0; gridIdx < gridElements.length; gridIdx++) {
            const gridEl = gridElements[gridIdx];
            const grid = new Grid(gridIdx);

            // Note: Grid margins (vbehúzás, fbehúzás) are recalculated by initMargins()
            // based on weak lines, so we don't need to parse them here.

            // Parse rows - Note: XML uses Hungarian element name with diacritic
            const rowElements = gridEl.getElementsByTagName('sor');
            const rows = rowElements.length;
            let columns = 0;

            grid.minId = cellIdCounter;
            grid.rows = rows;

            for (let rowIdx = 0; rowIdx < rowElements.length; rowIdx++) {
                const rowEl = rowElements[rowIdx];
                // XML uses 'mező' (with accent) for cell elements
                const cellElements = rowEl.getElementsByTagName('mező');

                if (rowIdx === 0) {
                    columns = cellElements.length;
                }

                for (let colIdx = 0; colIdx < cellElements.length; colIdx++) {
                    const cellEl = cellElements[colIdx];
                    const cell = Puzzle.parseCellFromXml(cellEl, cellIdCounter);

                    // Set inline styles for cell positioning and borders
                    // First cell in each row needs row break and left border
                    if (colIdx === 0) {
                        cell.setRowBreak();
                        cell.setBorderLeft();
                    }
                    // Very first cell in grid needs both borders
                    if (cellIdCounter === grid.minId) {
                        cell.setBorderLeft();
                        cell.setBorderTop();
                    }
                    // First row cells need top border
                    if (rowIdx === 0) {
                        cell.setBorderTop();
                    }

                    grid.push(cell);
                    cellIdCounter++;
                }
            }

            grid.columns = columns;
            grid.maxId = cellIdCounter - 1;

            puzzle.push(grid);
        }

        // Set total cell count
        puzzle.length = cellIdCounter;

        // Parse mask images
        if (grafikaEl) {
            puzzle.setMaskImagesFromXml(grafikaEl);
        }

        // Initialize (order matters - matches legacy initKifejezesHibak/initBehuzas/initVonalak)
        puzzle.initExpressionErrors();
        puzzle.initMargins();
        puzzle.initLines();

        return puzzle;
    }

    /**
     * Parse a cell from XML element
     * Note: XML uses Hungarian attribute names with diacritics
     */
    private static parseCellFromXml(cellEl: Element, id: number): Cell {
        // Cell value is the text content of the element, not an attribute
        const value = cellEl.textContent ?? '';

        return new Cell({
            id,
            value,
            type: cellEl.getAttribute('típus') ?? '',
            line: cellEl.getAttribute('vonal') ?? '',
            weakLine: cellEl.getAttribute('gyengevonal') ?? '',
            verticalClue: cellEl.getAttribute('vmeghatározás') ?? '',
            horizontalClue: cellEl.getAttribute('fmeghatározás') ?? '',
            verticalBackClue: cellEl.getAttribute('vhmeghatározás') ?? '',
            horizontalBackClue: cellEl.getAttribute('fhmeghatározás') ?? '',
            verticalExpression: cellEl.getAttribute('vkifejezés') ?? '',
            horizontalExpression: cellEl.getAttribute('fkifejezés') ?? '',
            verticalBackExpression: cellEl.getAttribute('vhkifejezés') ?? '',
            horizontalBackExpression: cellEl.getAttribute('fhkifejezés') ?? '',
            bridgePosition: cellEl.getAttribute('hídpozíció') ?? '',
            isKnown: parseInt(cellEl.getAttribute('ismert') ?? '0', 10),
        });
    }
}
